import React, { useCallback, useMemo, useState } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { Row, Col, Button, Spinner, Card, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import MetersPageShell from './components/MetersPageShell';
import RefreshControl from './components/RefreshControl';
import Header from '../../../component/header/header';
import MeterHero from './components/details/MeterHero';
import MeterIdentityStrip from './components/details/MeterIdentityStrip';
import DowntimeAlerts from './components/details/DowntimeAlerts';
import DataCompleteness from './components/details/DataCompleteness';
import QuickStatsSection from './components/details/QuickStatsSection';
import MeterGraphCard from './components/details/MeterGraphCard';
import { useMeter } from './hooks/useMeters';
import { latestFinancialYear, findKpiForYear } from './utils/meterFilters';
import { useFinancialYears, useSources } from '../../../hooks/useApiData';
import { DETAIL_GRAPHS } from './components/details/detailGraphs';
import ExecSummaryBanner from '../../../globalComponents/insights/ExecSummaryBanner';
import './styles/MeterDetails.css';
import './styles/Insights.css';

const MeterDetails = () => {
  const { meterId } = useParams();
  const [showInsights, setShowInsights] = useState(false);
  const [narratives, setNarratives] = useState([]);
  const history = useHistory();
  const location = useLocation();
  const sources = useSources();
  const financialYears = useFinancialYears();
  const currentFY = useMemo(
    () => latestFinancialYear(financialYears),
    [financialYears]
  );


  // Fetch meter details
  const { data: meter, isLoading: meterLoading, error: meterError, refetch: refetchMeter } = useMeter(meterId);

  const currentSource = sources?.find(s => Number(s.id) === Number(meter?.sourceId));
  const currentSubLocation = currentSource?.subLocation?.find(sl => Number(sl.id) === Number(meter?.subLocationId))

  // Get current financial year KPI
  const currentFYKpi = useMemo(
    () => (meter ? findKpiForYear(meter, currentFY) : null),
    [meter, currentFY]
  );


  /**
   * Keyed by chart, because a chart reports again whenever its own range
   * changes and two reports for one chart would be summarised as two findings.
   */
  const handleInsightsReady = useCallback((reported) => {
    setNarratives((current) => [
      ...current.filter((entry) => entry.graphId !== reported.graphId),
      reported,
    ]);
  }, []);

  /**
   * Returns to the meters list carrying whatever filters were applied when the
   * meter was opened, so the list is not silently reset.
   */
  /**
   * Clearing on both directions is deliberate: the summary is written from the
   * narratives it was given, so leaving them in place would have the banner
   * reappear on the next switch-on describing charts since refetched.
   */
  const toggleInsights = () => {
    setShowInsights((current) => !current);
    setNarratives([]);
  };

  const handleBackToList = () => {
    history.push({ pathname: '/iot-meters', search: location.search });
  };

  if (meterLoading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading meter details...</p>
      </div>
    );
  }

  if (meterError || !meter) {
    return (
      <div className="text-center p-5">
        <Alert variant="danger">
          Failed to load meter details. Please try again.
        </Alert>
        <Button variant="primary" onClick={() => handleBackToList()}>
          Back to Meters
        </Button>
      </div>
    );
  }

  if (!currentFYKpi) {
    return (
      <div className="meter-details-page">
        <MetersPageShell contentClassName="content-area im-details p-0">
            <div style={{ position: 'sticky', top: 0, zIndex: 999 }}>
              <Header hideBack />
            </div>
            
            <div className="content-wrapper">
              <div className="page-header">
                <Button 
                  variant="outline-secondary" 
                  size="sm"
                  onClick={() => handleBackToList()}
                  className="mb-3"
                >
                  <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                  Back to Meters
                </Button>
                
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h1>{meter.meterName}</h1>
                    <p className="mb-0">
                      {meter.meterType} • {meter.unit}
                    </p>
                  </div>
                </div>
              </div>

              <Alert variant="warning" className="mt-4">
                <Alert.Heading>KPI Configuration Required</Alert.Heading>
                <p>
                  No KPI has been configured for this meter in the current financial year ({currentFY}).
                  Please configure a KPI to view analytics and consumption data.
                </p>
                <hr />
                <div className="d-flex justify-content-end">
                  <Button 
                    variant="outline-warning"
                    onClick={() => handleBackToList()}
                  >
                    Go to Meters List to Configure KPI
                  </Button>
                </div>
              </Alert>

              <Card className="mt-3">
                <Card.Header>
                  <h5>Meter Information</h5>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <p><strong>Meter ID:</strong> {meter.meterId}</p>
                      <p><strong>Gateway ID:</strong> {meter.gatewayId}</p>
                      <p><strong>Meter Type:</strong> {meter.meterType}</p>
                      <p><strong>Measurement Type:</strong> {meter.measurementType}</p>
                    </Col>
                    <Col md={6}>
                      <p><strong>Unit:</strong> {meter.unit}</p>
                      <p><strong>Location:</strong> {currentSource?.unitCode || currentSource?.location?.area || meter.sourceId}</p>
                      <p><strong>Sub Location:</strong> {currentSubLocation?.subLocation || meter.subLocationId}</p>
                      <p><strong>Status:</strong> {meter.isActive ? 'Active' : 'Inactive'}</p>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </div>
        </MetersPageShell>
      </div>
    );
  }

  return (
    <div className="meter-details-page">
      <MetersPageShell contentClassName="content-area im-details p-0">
          <div style={{ position: 'sticky', top: 0, zIndex: 999 }}>
            <Header hideBack />
          </div>
          
          <div className="content-wrapper">
            {/* Page Header */}
            <div className="page-header-new">
              <Button 
                variant="link" 
                className="back-button"
                onClick={() => handleBackToList()}
              >
                <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                Back to Meters
              </Button>
              
              <div className="header-controls">
                <div className="im-insights-toggle">
                  <input
                    id="md-insights"
                    type="checkbox"
                    role="switch"
                    checked={showInsights}
                    onChange={toggleInsights}
                  />
                  <label htmlFor="md-insights">
                    AI insights
                  </label>
                </div>

                <RefreshControl onRefresh={refetchMeter} />
              </div>
            </div>

            <MeterHero meter={meter} currentFY={currentFY} />

            <h2 className="section-title">Identity</h2>

            <MeterIdentityStrip
              meter={meter}
              location={currentSource?.unitCode || currentSource?.location?.area}
              subLocation={currentSubLocation?.subLocation}
            />


            <h2 className="section-title">Consumption Heatmap</h2>

            <DataCompleteness meterId={meter.meterId} />

            <QuickStatsSection 
              meter={meter}
              currentFY={currentFY}
              currentFYKpi={currentFYKpi}
            />

            {/* Live Graphs Section */}
            <div className="graphs-section">
              <h2 className="section-title">Live Monitoring</h2>
              
              {showInsights && (
                <ExecSummaryBanner
                  resolvedInsights={narratives}
                  totalGraphCount={DETAIL_GRAPHS.length}
                  tabName={`meter-details · ${meter.meterName || meter.meterId}`}
                />
              )}

              {DETAIL_GRAPHS.map((graph) => (
                <MeterGraphCard
                  key={graph.graphType}
                  currentFY={currentFY}
                  meter={meter}
                  currentFYKpi={currentFYKpi}
                  showInsights={showInsights}
                  onInsightsReady={handleInsightsReady}
                  {...graph}
                />
              ))}
            </div>

            <h2 className="section-title">Downtime &amp; Alerts</h2>

            <DowntimeAlerts meterId={meter.meterId} />
          </div>
      </MetersPageShell>
    </div>
  );
};

export default MeterDetails;
